import React, { ReactNode } from "react";
import { BackofficeUser } from "../types/BackofficeUser";
import { useDrawer } from "../provider/DrawerProvider";
import EditActivity from "../components/Activities/EditActivity/EditActivity";
import CreateActivity from "../components/Activities/EditActivity/CreateActivity";
import ComposeEmailOrMessageForm from "../components/Case/CaseWriteEmail/ComposeEmailOrMessageForm";
import EditNote from "../components/Case/CaseCreateNote/EditNote";
import CommunicationMedium from "../components/Case/CaseBeaAndFax/CommunicationMedium";
import AddInquiryToCustomerDrawer from "../components/Case/CaseModals/AddInquiryToCustomerDrawer";
import { ProductUnion } from "../types/ProductUnion";
import CaseDescriptionEdit from "../components/Case/CaseDescriptionEdit/CaseDescriptionEdit";
import DocumentEditorPanel from "../components/DocumentEditor/DocumentEditorPanel";
import { getIriOfSubEntity } from "../services/utils";
import { translate } from "../services/Translations/translatorService.tsx";

type UseDrawerConfigProps = {
  product: ProductUnion | null;
  currentUser: BackofficeUser;
  refreshActivities: Function;
  defaultAssignedUser: string;
};

export const useCaseRelatedDrawerConfig = ({
  product,
  currentUser,
  refreshActivities,
  defaultAssignedUser,
}: UseDrawerConfigProps): { title: string; content: ReactNode } | null => {
  const { drawerState, closeDrawer } = useDrawer();

  if (!product) {
    return null;
  }

  const entityTranslation = translate("drawer.type." + drawerState?.type, "N/A");

  switch (drawerState?.type) {
    case "activity": {
      if (currentUser.isExternal) {
        return null;
      }

      const activity = drawerState?.entityData;
      const isEdit = !!activity;

      return {
        title: entityTranslation + (isEdit ? " bearbeiten" : " erstellen"),
        content: isEdit ? (
          <EditActivity
            renderInDrawer={true}
            activity={activity}
            closeDialog={closeDrawer}
            updateActivities={refreshActivities}
            productClassName={product.productClassName}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
            open={true}
          />
        ) : (
          <CreateActivity
            product={product}
            closeDialog={closeDrawer}
            updateActivities={() => refreshActivities()}
            caseId={product.backofficeCase["@id"]}
            defaultAssignedUser={defaultAssignedUser}
          />
        ),
      };
    }

    case "message": {
      const message = drawerState?.entityData;
      const isResponse = message && getIriOfSubEntity(message, "conversation");
      return {
        title: entityTranslation + (isResponse ? " beantworten" : " schreiben"),
        content: (
          <ComposeEmailOrMessageForm
            product={product}
            closeDialog={closeDrawer}
            customerCommunicationOnly={drawerState?.typeSpecificData?.customerCommunicationOnly}
            message={message}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
          />
        ),
      };
    }

    case "note": {
      const note = drawerState?.entityData;
      const isEdit = !!note;

      return {
        title: entityTranslation + (isEdit ? " bearbeiten" : " erstellen"),
        content: (
          <EditNote
            product={product}
            closeDialog={closeDrawer}
            note={note}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
          />
        ),
      };
    }

    case "beaFax": {
      if (currentUser.isExternal) {
        return null;
      }

      return {
        title: entityTranslation + " versenden",
        content: (
          <CommunicationMedium
            product={product}
            handleClose={closeDrawer}
            communicationDraft={drawerState?.entityData}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
          />
        ),
      };
    }

    case "inquiryToCustomer":
      return {
        title: entityTranslation + " stellen",
        content: (
          <AddInquiryToCustomerDrawer
            product={product}
            handleClose={closeDrawer}
            draft={drawerState?.entityData}
            conversationId={drawerState.typeSpecificData?.conversationId}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
          />
        ),
      };

    case "email": {
      if (currentUser.isExternal) {
        return null;
      }

      return {
        title: entityTranslation + " bearbeiten",
        content: (
          <ComposeEmailOrMessageForm
            product={product}
            closeDialog={closeDrawer}
            //the email drawer only exists for sendEmail execution steps whose payload must stay email-shaped
            emailCommunicationOnly={true}
            emailDraft={drawerState.entityData}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
          />
        ),
      };
    }

    case "caseDescription":
      return {
        title: entityTranslation,
        content: <CaseDescriptionEdit handleClose={closeDrawer} />,
      };

    case "documentEditor": {
      if (currentUser.isExternal) {
        return null;
      }
      // This drawer is opened from an execution step, which provides entityData.
      if (!drawerState.entityData) {
        return null;
      }

      return {
        title: entityTranslation + " bearbeiten",
        content: (
          <DocumentEditorPanel
            payload={drawerState.entityData}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
          />
        ),
      };
    }

    default:
      return null;
  }
};
